import {
  createDefaultHomeEconomy,
  normalizeHomeEconomy,
  setItemHotbarAutoHpPercent,
  setItemHotbarAutoManaPercent,
  type HomeEconomy,
} from "@/home/economy";
import { totalHealthPotions } from "@/home/health-potions";
import { HomeEvents } from "@/home/home-events";
import { loadHomeEconomyFromIdb, saveHomeEconomyToIdb } from "@/home/home-idb";
import { totalManaPotions } from "@/home/mana-potions";

export type ItemHotbarSettingsPanelOptions = {
  userId: string;
  /** Require explicit apply (character sheet tab). */
  applyOnButton?: boolean;
  onApplied?: (economy: HomeEconomy) => void;
};

function formatPotionStock(total: number): string {
  if (total <= 0) return "ไม่มียาในคลัง";
  return `พร้อมใช้ · คลัง ${total.toLocaleString("en-US")} ขวด`;
}

export function mountItemHotbarSettingsPanel(
  host: HTMLElement,
  options: ItemHotbarSettingsPanelOptions,
): () => void {
  const { userId, applyOnButton = false, onApplied } = options;
  let saved: HomeEconomy = createDefaultHomeEconomy();
  let draftHpPercent = 40;
  let draftManaPercent = 40;
  let loaded = false;

  host.innerHTML = `
    <section class="item-hotbar-settings game-auto-potions" aria-labelledby="item-hotbar-settings-title">
      <header class="game-auto-potions-head">
        <div class="game-auto-potions-emblem" aria-hidden="true">⚗</div>
        <div>
          <h2 id="item-hotbar-settings-title" class="item-hotbar-settings-title">ใช้ยาอัตโนมัติ</h2>
          <p class="game-auto-potions-tagline">ระบบจะใช้ยาจากคลังเมื่อเลือดหรือมานาต่ำกว่าเกณฑ์ — ไม่ต้องเลือกช่องแถบ</p>
        </div>
      </header>

      <div class="game-auto-potions-grid">
        <article class="game-auto-potion-card game-auto-potion-card--hp">
          <div class="game-auto-potion-card-top">
            <span class="game-auto-potion-icon" aria-hidden="true">♥</span>
            <div class="game-auto-potion-card-meta">
              <h3 class="game-auto-potion-card-title">ยาเลือด</h3>
              <p class="game-auto-potion-stock" id="item-hotbar-auto-hp-stock" role="status">—</p>
            </div>
            <output id="item-hotbar-auto-hp-out" class="game-auto-potion-value" for="item-hotbar-auto-hp-range">40%</output>
          </div>
          <label class="game-auto-potion-slider-wrap">
            <span class="visually-hidden">ใช้ยาเลือดเมื่อ HP ต่ำกว่า</span>
            <input
              type="range"
              id="item-hotbar-auto-hp-range"
              class="game-auto-potion-range game-auto-potion-range--hp"
              min="1"
              max="99"
              step="1"
              value="40"
              disabled
            />
          </label>
          <p class="game-auto-potion-hint">ใช้เมื่อ HP ต่ำกว่าเกณฑ์นี้</p>
        </article>

        <article class="game-auto-potion-card game-auto-potion-card--mp">
          <div class="game-auto-potion-card-top">
            <span class="game-auto-potion-icon" aria-hidden="true">◆</span>
            <div class="game-auto-potion-card-meta">
              <h3 class="game-auto-potion-card-title">ยามานา</h3>
              <p class="game-auto-potion-stock" id="item-hotbar-auto-mana-stock" role="status">—</p>
            </div>
            <output id="item-hotbar-auto-mana-out" class="game-auto-potion-value" for="item-hotbar-auto-mana-range">40%</output>
          </div>
          <label class="game-auto-potion-slider-wrap">
            <span class="visually-hidden">ใช้ยามานาเมื่อ MP ต่ำกว่า</span>
            <input
              type="range"
              id="item-hotbar-auto-mana-range"
              class="game-auto-potion-range game-auto-potion-range--mp"
              min="1"
              max="99"
              step="1"
              value="40"
              disabled
            />
          </label>
          <p class="game-auto-potion-hint">ใช้เมื่อ MP ต่ำกว่าเกณฑ์นี้</p>
        </article>
      </div>

      ${
        applyOnButton
          ? `<button type="button" class="game-auto-potions-apply" id="item-hotbar-settings-apply" disabled>
              <span class="game-auto-potions-apply-glow" aria-hidden="true"></span>
              นำไปใช้
            </button>`
          : ""
      }
      <p class="game-auto-potions-status hint" id="item-hotbar-settings-status" role="status">กำลังโหลด…</p>
    </section>
  `;

  const range = host.querySelector<HTMLInputElement>("#item-hotbar-auto-hp-range");
  const out = host.querySelector<HTMLElement>("#item-hotbar-auto-hp-out");
  const hpStock = host.querySelector<HTMLElement>("#item-hotbar-auto-hp-stock");
  const manaRange = host.querySelector<HTMLInputElement>("#item-hotbar-auto-mana-range");
  const manaOut = host.querySelector<HTMLElement>("#item-hotbar-auto-mana-out");
  const manaStock = host.querySelector<HTMLElement>("#item-hotbar-auto-mana-stock");
  const status = host.querySelector<HTMLElement>("#item-hotbar-settings-status");
  const applyBtn = host.querySelector<HTMLButtonElement>("#item-hotbar-settings-apply");

  const syncStockUi = (economy: HomeEconomy) => {
    const hpTotal = totalHealthPotions(economy.wallet);
    const mpTotal = totalManaPotions(economy.wallet);
    if (hpStock) {
      hpStock.textContent = formatPotionStock(hpTotal);
      hpStock.classList.toggle("game-auto-potion-stock--empty", hpTotal <= 0);
    }
    if (manaStock) {
      manaStock.textContent = formatPotionStock(mpTotal);
      manaStock.classList.toggle("game-auto-potion-stock--empty", mpTotal <= 0);
    }
  };

  const readDraftFromUi = () => {
    draftHpPercent = Number(range?.value ?? draftHpPercent);
    draftManaPercent = Number(manaRange?.value ?? draftManaPercent);
  };

  const syncUiFromDraft = () => {
    if (range) {
      range.value = String(draftHpPercent);
      range.disabled = !loaded;
      range.style.setProperty("--auto-potion-pct", `${draftHpPercent}%`);
    }
    if (out) out.textContent = `${draftHpPercent}%`;
    if (manaRange) {
      manaRange.value = String(draftManaPercent);
      manaRange.disabled = !loaded;
      manaRange.style.setProperty("--auto-potion-pct", `${draftManaPercent}%`);
    }
    if (manaOut) manaOut.textContent = `${draftManaPercent}%`;
    if (applyBtn) applyBtn.disabled = !loaded;
    syncStockUi(saved);
  };

  const loadFromSaved = () => {
    draftHpPercent = saved.itemHotbarAutoHpPercent;
    draftManaPercent = saved.itemHotbarAutoManaPercent;
    syncUiFromDraft();
  };

  const buildEconomyFromDraft = (): HomeEconomy => {
    let next = setItemHotbarAutoHpPercent(saved, draftHpPercent);
    next = setItemHotbarAutoManaPercent(next, draftManaPercent);
    return next;
  };

  const persist = async (next: HomeEconomy) => {
    saved = next;
    await saveHomeEconomyToIdb(userId, next);
    window.dispatchEvent(
      new CustomEvent(HomeEvents.EconomyChanged, { detail: { economy: next } }),
    );
    onApplied?.(next);
    syncStockUi(saved);
  };

  const onEconomyChanged = (event: Event) => {
    const detail = (event as CustomEvent<{ economy?: HomeEconomy }>).detail;
    if (!detail?.economy) return;
    saved = normalizeHomeEconomy(detail.economy);
    syncStockUi(saved);
  };

  window.addEventListener(HomeEvents.EconomyChanged, onEconomyChanged);

  void (async () => {
    const raw = await loadHomeEconomyFromIdb(userId);
    saved = raw ? normalizeHomeEconomy(raw) : createDefaultHomeEconomy();
    loaded = true;
    loadFromSaved();
    if (status) {
      status.textContent = applyOnButton
        ? "ปรับเกณฑ์แล้วกดนำไปใช้"
        : "บันทึกอัตโนมัติเมื่อปรับค่า";
    }
  })();

  const onDraftChange = () => {
    if (!loaded) return;
    readDraftFromUi();
    syncUiFromDraft();
    if (!applyOnButton) {
      void persist(buildEconomyFromDraft())
        .then(() => {
          if (status) status.textContent = "บันทึกแล้ว";
        })
        .catch(() => {
          if (status) status.textContent = "บันทึกไม่สำเร็จ — ลองอีกครั้ง";
        });
    } else if (status) {
      status.textContent = "มีการเปลี่ยนแปลง — กดนำไปใช้";
    }
  };

  range?.addEventListener("input", onDraftChange);
  manaRange?.addEventListener("input", onDraftChange);

  applyBtn?.addEventListener("click", () => {
    if (!loaded) return;
    readDraftFromUi();
    const next = buildEconomyFromDraft();
    void persist(next)
      .then(() => {
        if (status) status.textContent = "นำไปใช้แล้ว — ใช้ได้ในบ้านและหอคอย";
      })
      .catch(() => {
        if (status) status.textContent = "นำไปใช้ไม่สำเร็จ — ลองอีกครั้ง";
      });
  });

  return () => {
    window.removeEventListener(HomeEvents.EconomyChanged, onEconomyChanged);
    range?.replaceWith(range.cloneNode(true));
    manaRange?.replaceWith(manaRange.cloneNode(true));
    applyBtn?.replaceWith(applyBtn.cloneNode(true));
  };
}
